<template>
  <div class="table-container">
    <!-- 搜索栏（固定） -->
    <div class="search-container" :class="{ 'mobile-search-top': !isDesktop }">
      <!-- 电脑端使用 Element UI 搜索框 -->
      <div v-if="isDesktop" class="desktop-search-wrapper">
        <div class="search-row">
          <el-input 
            v-model="searchKeyword" 
            placeholder="请输入标签名搜索" 
            clearable
            size="small"
            class="search-input"
          >
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
          <div class="button-group">
            <el-button 
              type="primary" 
              size="small" 
              icon="el-icon-plus" 
              @click="handleAdd"
              class="add-button"
            >
              添加标签
            </el-button>
            <el-button 
              type="primary" 
              size="small" 
              icon="el-icon-filter" 
              @click="toggleFilter"
              class="filter-button"
            >
              {{ isFilterOpen ? '收起筛选' : '展开筛选' }}
            </el-button>
          </div>
        </div>
        
        <!-- 电脑端筛选面板 -->
        <div v-if="isFilterOpen" class="filter-panel">
          <div class="filter-row">
            <!-- 目前标签管理没有其他筛选条件，所以这里可以留空 -->
          </div>
          <div class="filter-actions">
            <div class="applied-filters">
              <span v-if="!searchKeyword" class="filter-tag filter-tag-empty">
                当前无筛选条件
              </span>
              <span v-else class="filter-tag">
                搜索关键词: {{ searchKeyword }}
              </span>
            </div>
            <div class="filter-buttons">
              <el-button size="small" @click="resetFilter">重置筛选</el-button>
              <el-button type="primary" size="small" @click="applyFilter">应用筛选</el-button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 手机端使用 Vant 搜索框 -->
      <div v-else class="mobile-search-wrapper">
        <van-search
          v-model="searchKeyword"
          placeholder="请输入标签名搜索"
          show-action
          @search="handleSearch"
          @cancel="onSearchCancel"
          :placeholder-style="{ fontSize: '16px' }"
          :input-align="'center'"
        >
          <template #action>
            <div @click="handleSearch" style="margin-right: 10px; font-size: 16px; color: var(--primary); font-weight: 600;">搜索</div>
          </template>
        </van-search>
        
        <!-- 手机端添加按钮 -->
        <div class="mobile-add-button">
          <van-button 
            type="primary" 
            size="small" 
            block
            icon="plus"
            @click="handleAdd"
            style="margin-top: 10px;"
          >
            添加标签
          </van-button>
        </div>
        
        <!-- 手机端筛选面板 -->
        <div v-if="isFilterOpen" class="mobile-filter-panel">
          <!-- 手机端已应用筛选条件显示 -->
          <div class="mobile-applied-filters">
            <div v-if="searchKeyword" class="applied-title">已应用筛选条件：</div>
            <div class="applied-tags">
              <span v-if="searchKeyword" class="mobile-filter-tag">
                搜索关键词: {{ searchKeyword }}
              </span>
              <span v-else class="mobile-filter-tag empty">
                当前无筛选条件
              </span>
            </div>
          </div>
          
          <!-- 目前标签管理没有其他筛选条件，所以这里可以留空 -->
          
          <div class="mobile-filter-actions">
            <van-button type="default" size="small" @click="resetFilter">重置</van-button>
            <van-button type="primary" size="small" @click="applyFilter">应用</van-button>
          </div>
        </div>
        
        <!-- 手机端筛选展开按钮 -->
        <div class="mobile-filter-toggle" @click="toggleFilter">
          <van-icon :name="isFilterOpen ? 'arrow-up' : 'arrow-down'" />
          <span>{{ isFilterOpen ? '收起筛选' : '展开筛选' }}</span>
        </div>
      </div>
    </div>
    
    <!-- 内容区域（可滚动） -->
    <div class="content-container">
      <!-- 电脑端表格 -->
      <div class="table-wrapper" v-if="isDesktop">
        <el-table 
          :data="filteredData" 
          style="width: 100%" 
          class="desktop-table"
          :height="tableHeight"
          tooltip-effect="dark"
          empty-text="暂无数据"
        >
        <el-table-column label="标签ID" prop="tag_id" align="left" fixed width="160px"></el-table-column>
        <el-table-column label="标签名" prop="name" align="left" fixed width="160px"></el-table-column>
        <el-table-column label="颜色" prop="color" align="left" width="200px">
          <template slot-scope="scope">
            <div style="display:inline-flex;align-items:center;">
              <div :style="{'backgroundColor':scope.row.color}" style="width:20px;height:20px;border-radius:4px;margin-right:10px;"></div>
              {{scope.row.color}}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="描述" prop="description" align="left"></el-table-column>
        <el-table-column label="操作" width="155" fixed="right">
          <template slot-scope="scope">
            <div class="table-actions">
              <el-link @click="editChild(scope.row)" >
                <i class="el-icon-edit"></i>编辑
              </el-link>
              <el-dropdown @command="command($event,scope.row)">
                  <span class="el-dropdown-link">
                    更多操作
                    <i class="el-icon-arrow-down el-icon--right"></i>
                  </span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="remove" >删除</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </template>
        </el-table-column>
      </el-table>
      </div>
      
      <!-- 手机端卡片 -->
      <div v-else class="mobile-cards">
        <div 
          v-for="item in filteredData" 
          :key="item.tag_id" 
          class="mobile-card"
        >
          <div class="card-header">
            <h3>{{ item.name }}</h3>
          </div>
          <div class="card-content">
            <p><strong>标签ID:</strong> {{ item.tag_id }}</p>
            <p><strong>颜色:</strong> 
              <span style="display:inline-flex;align-items:center;">
                <div :style="{'backgroundColor':item.color}" style="width:16px;height:16px;border-radius:3px;margin-right:8px;"></div>
                <span>{{ item.color }}</span>
              </span>
            </p>
            <p><strong>描述:</strong> {{ item.description || '-' }}</p>
          </div>
          <div class="card-actions">
            <van-button 
              type="primary" 
              size="small" 
              plain
              icon="edit"
              @click="editChild(item)"
              style="margin-right: 10px;"
            >
              编辑
            </van-button>
            <van-button 
              type="default" 
              size="small" 
              plain
              icon="ellipsis"
              @click="showMoreActions(item)"
            >
              更多操作
            </van-button>
          </div>
        </div>
        
        <!-- 手机端空状态 -->
        <div v-if="filteredData.length === 0" class="mobile-empty">
          暂无数据
        </div>
        
        <!-- 手机端操作菜单，只显示一次 -->
        <van-action-sheet
          v-model="showActionSheet"
          get-container="body"
          :actions="actionSheetActions"
          @select="onActionSheetSelect"
          @close="onActionSheetClose"
        />
      </div>
    </div>
    
    <!-- 分页（固定） -->
    <div class="pagination-container">
      <el-pagination
        v-if="isDesktop"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="filteredData.length"
      ></el-pagination>
      
      <el-pagination
        v-else
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[5, 10, 15]"
        :page-size="pageSize"
        layout="total, prev, pager, next"
        :total="filteredData.length"
        small
      ></el-pagination>
    </div>
    
    <!-- 添加活动信息 -->
    <el-drawer v-if="isDesktop" title="添加标签" :visible.sync="addDrawer" :direction="direction" :before-close="handleClose">
      <add
        @close="handleClose"
        @success="handleDrawerSuccess"
        ref="newForm"
      ></add>
    </el-drawer>

    <!-- 修改活动信息 -->
    <el-drawer v-if="isDesktop" title="更改标签" :visible.sync="editDrawer" :direction="direction" :before-close="handleClose">
      <edit
        ref="newForm"
        v-if="editDrawer"
        @close="handleClose"
        @success="handleDrawerSuccess"
        :id="editMenu.id"
        :changeInfo="changeInfo"
      ></edit>
    </el-drawer>
    
    <!-- 添加标签弹窗 - 手机端 -->
    <van-popup
      v-if="!isDesktop" 
      v-model="addPopup" 
      position="bottom" 
      :round="true"
      :close-on-click-overlay="false"
      get-container="body"
      style="max-height:80%"
      @open="handlePopupOpen"
      @close="handlePopupClose"
    >
      <div class="popup-container">
        <div class="popup-header">
          <h3>添加标签</h3>
          <van-icon name="cross" @click="handleClosePopup('add')" class="close-icon" />
        </div>
        <div class="popup-content">
          <add @close="handleClosePopup('add')" @success="handlePopupSuccess" ref="newForm"></add>
        </div>
      </div>
    </van-popup>
    
    <!-- 修改标签弹窗 - 手机端 -->
    <van-popup
      v-if="!isDesktop" 
      v-model="editPopup" 
      position="bottom" 
      :round="true"
      :close-on-click-overlay="false"
      get-container="body"
      style="max-height:80%"
      @open="handlePopupOpen"
      @close="handlePopupClose"
    >
      <div class="popup-container">
        <div class="popup-header">
          <h3>更改标签</h3>
          <van-icon name="cross" @click="handleClosePopup('edit')" class="close-icon" />
        </div>
        <div class="popup-content">
          <edit @close="handleClosePopup('edit')" @success="handlePopupSuccess" :id="editMenu.id" :changeInfo="changeInfo" ref="newForm"></edit>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
// 静态导入 Vant 弹框组件
import { Dialog, Toast, Icon } from 'vant'
import {getTags,deleteTag} from '@/api/system'
import edit from "./components/edit.vue";
import add from "./components/add.vue";

export default {
  name: "newindex",
  components: { add, edit, [Icon.name]: Icon },
  data() {
    return {
      searchKeyword: '',
      currentPage: 1,
      pageSize: 10,
      isDesktop: true,
      // 筛选相关
      isFilterOpen: false,
      filterForm: {
        // 标签管理目前没有额外的筛选条件
      },
      // 保存已应用的筛选条件
      appliedFilterForm: {
        // 标签管理目前没有额外的筛选条件
      },
      // 手机端操作菜单
      showActionSheet: false,
      currentRow: null,
      actionSheetActions: [
        { name: '删除', type: 'danger' }
      ],
      // 表格数据
      tableData: [],
      // 标签相关数据
      addSonMenu: {
        parent_id: 0,
        parent_name: ''
      },
      editMenu: {
        id: 0
      },
      show: false,
      total: 0,
      addDrawer: false,
      editDrawer: false,
      addPopup: false,
      editPopup: false,
      direction: "rtl",
      allInfo: [],
      drawer: false,
      options3: [],
      close: false,
      changeInfo: ""
    };
  },
  computed: {
    filteredData() {
      return this.tableData.filter(item => {
        // 关键词搜索
        const keyword = this.searchKeyword.toLowerCase()
        const matchesKeyword = !keyword || 
                             item.name.toLowerCase().includes(keyword) ||
                             item.tag_id.toString().includes(keyword)
        return matchesKeyword
      })
    },
    tableHeight() {
      if (!this.isDesktop) return 'auto'
      // 计算表格高度：减去顶部栏、筛选区域、分页栏和底部的高度
      const headerHeight = 60 // 顶部栏高度
      const searchHeight = this.isFilterOpen ? 180 : 60 // 搜索栏高度（包含筛选面板）
      const paginationHeight = 60 // 分页栏高度
      const footerHeight = 40 // 底部高度
      const padding = 20 // 页面内边距
      return window.innerHeight - headerHeight - searchHeight - paginationHeight - footerHeight - padding - 100 // 100是其他元素的额外高度
    }
  },
  mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.handleResize)
    this.getTableData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.checkScreenSize()
    },
    checkScreenSize() {
      this.isDesktop = window.innerWidth > 768
    },
    handleSearch() {
      this.currentPage = 1
    },
    // 处理添加按钮点击
    handleAdd() {
      if (this.isDesktop) {
        this.addDrawer = true
      } else {
        this.addPopup = true
      }
    },
    // 切换筛选面板
    toggleFilter() {
      this.isFilterOpen = !this.isFilterOpen
    },
    // 应用筛选
    applyFilter() {
      // 保存当前筛选条件为已应用条件
      this.appliedFilterForm = JSON.parse(JSON.stringify(this.filterForm))
      this.currentPage = 1
    },
    // 重置筛选
    resetFilter() {
      // 初始化筛选条件
      const initialFilter = {}
      // 同时重置当前筛选表单和已应用筛选条件
      this.filterForm = JSON.parse(JSON.stringify(initialFilter))
      this.appliedFilterForm = JSON.parse(JSON.stringify(initialFilter))
      this.searchKeyword = ''
      this.currentPage = 1
    },
    // 获取表格数据
    getTableData() {
      getTags({}).then(res => {
        if (res.code === 200) {
          this.tableData = res.data
        }
      })
    },
    // 删除标签
    handleDelete(id) {
      if (this.isDesktop) {
        // 电脑端使用 Element UI 弹框
        this.$confirm(`确定要删除该标签吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          deleteTag(id).then(res => {
            this.getTableData()
            this.$message({
              title: '成功',
              message: '删除成功',
              type: 'success'
            })
          })
        }).catch(() => {
          this.$message({
            type: 'info',
            message: '已取消删除'
          })
        })
      } else {
        // 手机端使用 Vant Dialog
        Dialog.confirm({
          title: '提示',
          message: `确定要删除该标签吗？`,
          confirmButtonText: '确定',
          cancelButtonText: '取消',
        })
        .then(() => {
          // 确认删除
          deleteTag(id).then(res => {
            this.getTableData()
            Toast({
              message: '删除成功',
              type: 'success',
              duration: 2000
            })
          })
        })
        .catch(() => {
          // 取消删除
          Toast({
            message: '已取消删除',
            type: 'info',
            duration: 2000
          })
        })
      }
    },
    // 处理更多操作命令
    command(cmd, row) {
      if (cmd === 'remove') {
        this.handleDelete(row.id)
      }
    },
    // 显示手机端更多操作菜单
    showMoreActions(row) {
      this.currentRow = row
      this.showActionSheet = true
    },
    // 处理手机端操作菜单选择
    onActionSheetSelect(action, index) {
      if (action.name === '删除') {
        this.handleDelete(this.currentRow.id)
      }
      this.showActionSheet = false
    },
    // 关闭手机端操作菜单
    onActionSheetClose() {
      this.currentRow = null
    },
    handleSizeChange(val) {
      this.pageSize = val
    },
    handleCurrentChange(val) {
      this.currentPage = val
    },
    // 处理 Vant 搜索框取消事件
    onSearchCancel() {
      this.searchKeyword = ''
    },
    // 修改信息
    handleEdit(index, row) {
      this.id = row.id;
      this.editDrawer = true;
      this.changeInfo = row;
    },
    addChild(value='') {
      if(value){
        this.addSonMenu = {
          parent_id : value.id,
          parent_name: value.menu_id
        }
      }
      if (this.isDesktop) {
        this.addDrawer = true
      } else {
        this.addPopup = true
      }
    },
    editChild(value) {
      this.editMenu = {
        id : value.id,
      }
      this.changeInfo = value
      if (this.isDesktop) {
        this.editDrawer = true
      } else {
        this.editPopup = true
      }
    },
    // 关闭
    handleClose() {
      this.$confirm("确认关闭？")
        .then(() => {
          this.addDrawer = false;
          this.editDrawer = false;
        })
        .catch(() => {});
    },
    // 关闭弹窗 - 手机端
    handleClosePopup(type) {
      Dialog.confirm({
        title: '提示',
        message: '确认关闭？',
        confirmButtonText: '确定',
        cancelButtonText: '取消',
      })
      .then(() => {
        if (type === 'add') {
          this.addPopup = false;
        } else if (type === 'edit') {
          this.editPopup = false;
        }
      })
      .catch(() => {
        // 取消关闭
      })
    },
    
    // 弹窗打开时禁止页面滚动
    handlePopupOpen() {
      document.body.style.overflow = 'hidden';
    },
    
    // 弹窗关闭时恢复页面滚动
    handlePopupClose() {
      document.body.style.overflow = '';
    },
    // 添加/修改成功 - 电脑端
    handleDrawerSuccess() {
      this.getTableData();
      this.addDrawer = false;
      this.editDrawer = false;
      if (this.$refs.newForm && this.$refs.newForm.$refs.form) {
        this.$refs.newForm.$refs.form.resetFields();//清空子组件的内容
      }
    },
    // 添加/修改成功 - 手机端
    handlePopupSuccess() {
      this.getTableData();
      this.addPopup = false;
      this.editPopup = false;
      if (this.$refs.newForm && this.$refs.newForm.$refs.form) {
        this.$refs.newForm.$refs.form.resetFields();//清空子组件的内容
      }
    }
  },
  mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.handleResize)
    this.getTableData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style scoped>
/* 使用全局样式中定义的 CSS 变量 */
.table-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* 搜索栏（固定） */
.search-container {
  margin-bottom: 0;
  padding: 20px;
  background-color: var(--background);
  border-radius: var(--border-radius);
  box-sizing: border-box;
  transition: all 0.3s ease;
}

/* 手机端搜索置顶 */
.mobile-search-top {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 0;
  padding: 10px 15px;
}

.search-container h2 {
  margin-bottom: 20px;
  color: var(--text);
}

/* 电脑端搜索容器 */
.desktop-search-wrapper {
  width: 100%;
}

.search-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.search-input {
  width: 100%;
  max-width: 400px;
  font-size: var(--font-size-sm);
}

.filter-button {
  white-space: nowrap;
}

/* 按钮组样式 */
.button-group {
  display: flex;
  align-items: center;
}

.add-button {
  white-space: nowrap;
}

/* 手机端添加按钮样式 */
.mobile-add-button {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

/* 筛选面板 */
.filter-panel {
  background-color: var(--bg-100);
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  padding: 15px;
  margin-top: 10px;
  box-shadow: var(--shadow);
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 15px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 筛选操作区域 */
.filter-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid var(--border);
}

/* 已应用筛选标签容器 */
.applied-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

/* 单个筛选标签样式 */
.filter-tag {
  background-color: var(--primary);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: var(--font-size-sm);
  display: inline-flex;
  align-items: center;
}

/* 空状态筛选标签 */
.filter-tag-empty {
  background-color: var(--bg-200);
  color: var(--text-200);
}

/* 筛选按钮容器 */
.filter-buttons {
  display: flex;
  gap: 10px;
}

.filter-item label {
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--text);
}

/* 手机端已应用筛选条件样式 */
.mobile-applied-filters {
  padding: 10px 15px;
  background-color: var(--bg-100);
  border-radius: var(--border-radius);
  margin-bottom: 15px;
}

.mobile-applied-filters .applied-title {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--text);
  margin-bottom: 8px;
}

.mobile-applied-filters .applied-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mobile-filter-tag {
  background-color: var(--primary);
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: var(--font-size-xs);
  display: inline-block;
}

.mobile-filter-tag.empty {
  background-color: var(--bg-200);
  color: var(--text-200);
}

/* 手机端筛选操作按钮区域 */
.mobile-filter-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 0;
  margin-top: 10px;
  border-top: 1px solid var(--border);
}

/* 手机端搜索容器 */
.mobile-search-wrapper {
  width: 100%;
}

.mobile-search-container {
  margin-bottom: 15px;
}

/* 手机端筛选面板 */
.mobile-filter-panel {
  background-color: var(--bg-100);
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  margin-top: 10px;
  overflow: hidden;
}

/* 手机端筛选展开按钮 */
.mobile-filter-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 10px;
  margin-top: 10px;
  background-color: var(--bg-100);
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  color: var(--text-200);
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-filter-toggle:hover {
  background-color: var(--border-light);
}

/* 内容区域（可滚动） */
.content-container {
  flex: 1;
  height: 100%;
  overflow: hidden;
  padding: 20px 0;
  padding-bottom: 0;
}

/* 电脑端表格 */
.table-wrapper {
  height: 100%;
  margin-bottom: 0;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  font-size: 14px;
}

.table-actions {
  width: 100%;
  display: inline-flex;
  justify-content: flex-end;
  gap: 10px;
  font-size: 14px;
}

.desktop-table {
  height: 100%;
  margin-bottom: 0;
  font-size: 14px;
}

/* 手机端卡片 */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 0;
}

.mobile-card {
  border: 1px solid var(--border);
  border-radius: var(--border-radius-lg);
  padding: 15px;
  background-color: var(--bg-200);
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}

.mobile-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-light);
}

.card-header h3 {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-content {
  border-bottom: 1px solid var(--border-light);
  padding-bottom: 12px;
  margin-bottom: 12px;
}

.card-content p {
  margin: 8px 0;
  font-size: var(--font-size-sm);
  color: var(--text-200);
  display: flex;
  justify-content: space-between;
  align-items: center;
  line-height: 1.6;
  flex-wrap: wrap;
}

.card-content p strong {
  color: var(--text);
  font-weight: 500;
  min-width: 80px;
  text-align: left;
  flex-shrink: 0;
}

.card-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 12px;
  flex-wrap: wrap;
  align-items: center;
}

/* Vant按钮样式优化 */
.card-actions .van-button {
  font-size: 13px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

/* 手机端空状态 */
.mobile-empty {
  text-align: center;
  padding: 40px 0;
  color: var(--text-200);
  font-size: var(--font-size-sm);
}

/* 适配小屏幕，确保操作按钮不会换行 */
@media (max-width: 375px) {
  .card-actions {
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }
  
  .card-actions .van-button {
    width: 100%;
    margin-right: 0 !important;
    margin-bottom: 8px;
  }
  
  .card-actions .van-dropdown-menu {
    width: 100%;
  }
}

/* 手机端弹窗样式 */
.popup-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--background);
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: var(--background);
  border-bottom: 1px solid var(--border);
  position: sticky;
  top: 0;
  z-index: 10;
}

.popup-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--text);
}

.popup-header .close-icon {
  font-size: 20px;
  color: var(--text-200);
  cursor: pointer;
  transition: color 0.2s ease;
}

.popup-header .close-icon:hover {
  color: var(--text);
}

.popup-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: var(--background);
}

/* 分页（固定） */
.pagination-container {
  margin-top: 0;
  margin-bottom: 0;
  padding: 10px;
  border-top: 1px solid var(--border);
  text-align: right;
  box-sizing: border-box;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .search-container h2 {
    margin-bottom: 15px;
  }
  
  .search-container .search-input {
    display: none;
  }
  
  .content-container {
    padding: 10px 0;
  }
  
  .search-input {
    max-width: 100%;
  }
  
  .pagination-container {
    text-align: center;
    padding: 15px;
  }
  
  .filter-row {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
  
  .filter-item {
    width: 100%;
  }
  
  .filter-item .el-select,
  .filter-item .el-input-number {
    width: 100% !important;
  }
}

@media (min-width: 769px) {
  .search-container .mobile-search-container,
  .mobile-filter-toggle {
    display: none;
  }
  
  .desktop-table {
    display: block;
  }
  
  .mobile-cards {
    display: none;
  }
}

@media (max-width: 768px) {
  .search-container {
    width: 100vw;
    margin-left: -15px;
    border-bottom-left-radius: var(--border-radius);
    border-bottom-right-radius: var(--border-radius);
    background-color: var(--bg-200);
  }
  .van-search {
    padding: 0;
  }
  .desktop-table {
    display: none;
  }
  
  .mobile-cards {
    display: flex;
  }
  
  .search-row {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .search-input {
    max-width: 100%;
  }
}
</style>